import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faMinus, faPlus, faShieldHalved, faStore } from '@fortawesome/free-solid-svg-icons';

import { InfoCuentasContext } from '../../Hooks/InfoCuentasContext';
import Carrousel from '../Carrousels/Carrousel';
import Loader from '../Loader/Loader';
import './AvisoContent.css';

function AvisoContent({ id }) {
  const [avisoInfo, setAvisoInfo] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [message, setMessage] = useState('');
  const [pathfiltro, setPathfiltro] = useState('');
  const [loadingCart, setLoadingCart] = useState(false);
  const { isAuthenticated } = useContext(InfoCuentasContext);

  const precio = Number(avisoInfo?.Precio || 0);
  const subtotal = precio * cantidad;

  const imagenes = useMemo(() => {
    if (!avisoInfo) {
      return [];
    }

    return Object.entries(avisoInfo)
      .filter(([key, value]) => key.toLowerCase().startsWith('imagen') && typeof value === 'string' && value)
      .map(([, value]) => value);
  }, [avisoInfo]);

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(valor);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/infoavisos/${id}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'No pudimos cargar el aviso.');
        }

        setAvisoInfo(data);

        const filtros = JSON.parse(localStorage.getItem('filtrosProductos') || '[]');
        const filtroOriginal = filtros.find((filtro) => filtro.idfiltro === data.Idfiltro);
        const filtroPadre = filtros.find((filtro) => filtro.idfiltro === filtroOriginal?.idfiltropadre);

        if (filtroOriginal && filtroPadre) {
          setPathfiltro(`${filtroPadre.nombre} / ${filtroOriginal.nombre}`);
        } else if (filtroOriginal) {
          setPathfiltro(filtroOriginal.nombre);
        }
      } catch (error) {
        console.error('Error al obtener datos:', error);
        setMessage(error.message);
      }
    };

    fetchData();
  }, [id]);

  const restarUnidad = () => {
    setCantidad((actual) => Math.max(1, actual - 1));
  };

  const sumarUnidad = () => {
    setCantidad((actual) => actual + 1);
  };

  const agregarAlCarrito = async () => {
    setShowOverlay(true);
    setShowButtons(false);
    setMessage('');

    if (!isAuthenticated) {
      setMessage('Debés iniciar sesión para agregar productos al carrito.');
      return;
    }

    try {
      setLoadingCart(true);
      const response = await fetch('/api/carrito', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'paste',
          avisos: [{
            avisoid: Number(id),
            cant: cantidad,
            precio,
            titulo: avisoInfo.Titulo,
            imagen: imagenes[0] || '',
          }],
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Error al agregar el producto al carrito.');
      }

      setShowButtons(true);
    } catch (error) {
      console.error('Error al agregar el producto al carrito:', error);
      setMessage(error.message);
    } finally {
      setLoadingCart(false);
    }
  };

  const comprarAhora = async () => {
    await agregarAlCarrito();
  };

  const handleOverlayClick = (action) => {
    setShowOverlay(false);
    if (action === 'ir') {
      window.location.href = '/tucarrito';
    } else if (action === 'seguir') {
      window.location.href = '/productos';
    }
  };

  if (!avisoInfo) {
    return (
      <main id="aviso-container" className="aviso-detail-page">
        <section className="aviso-loading">
          <Loader />
          <p>{message || 'Cargando información del aviso'}</p>
        </section>
      </main>
    );
  }

  return (
    <main id="aviso-container" className="aviso-detail-page">
      <section className="aviso-detail-shell">
        <div className="aviso-gallery">
          <Carrousel imagenes={imagenes} />
        </div>

        <article className="aviso-info">
          <div className="aviso-breadcrumb">{pathfiltro || 'Catálogo Cannubis'}</div>
          <h1>{avisoInfo.Titulo}</h1>
          <p className="aviso-description">{avisoInfo.Descripcion}</p>

          <div className="aviso-meta-grid">
            <div>
              <span>Publicado por</span>
              <strong><FontAwesomeIcon icon={faStore} /> {avisoInfo.Nombre || 'Tienda Cannubis'}</strong>
            </div>
            <div>
              <span>Stock disponible</span>
              <strong>{avisoInfo.Stockproducto || 0} unidades</strong>
            </div>
            <div>
              <span>Publicado el</span>
              <strong>{avisoInfo.Fechacreacion || 'Fecha no informada'}</strong>
            </div>
          </div>

          <div className="aviso-purchase-panel">
            <div>
              <span className="aviso-price-label">Precio</span>
              <strong className="aviso-price">{formatoPrecio(precio)}</strong>
            </div>

            <div className="aviso-quantity-row">
              <span>Cantidad</span>
              <div className="aviso-stepper" aria-label="Cantidad">
                <button type="button" onClick={restarUnidad} aria-label="Restar unidad">
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <output>{cantidad}</output>
                <button type="button" onClick={sumarUnidad} aria-label="Sumar unidad">
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>

            <div className="aviso-subtotal">
              <span>Subtotal</span>
              <strong>{formatoPrecio(subtotal)}</strong>
            </div>

            <div className="aviso-actions">
              <button className="aviso-primary" type="button" onClick={comprarAhora}>
                Comprar
              </button>
              <button className="aviso-secondary" disabled={loadingCart} type="button" onClick={agregarAlCarrito}>
                <FontAwesomeIcon icon={faCartShopping} />
                {loadingCart ? 'Agregando...' : 'Agregar al carrito'}
              </button>
            </div>

            <p className="aviso-trust">
              <FontAwesomeIcon icon={faShieldHalved} />
              Compra protegida por sesión y carrito asociado a tu cuenta.
            </p>
          </div>
        </article>
      </section>

      {showOverlay && (
        <div className="overlay">
          <div className="overlay-content">
            {message ? (
              <>
                <p>{message}</p>
                <button type="button" onClick={() => { window.location.href = '/login'; }}>Iniciar sesión</button>
              </>
            ) : (
              <>
                <p>Tu producto fue añadido al carrito.</p>
                {showButtons && (
                  <div className="buttons">
                    <button type="button" onClick={() => handleOverlayClick('ir')}>Ir al carrito</button>
                    <button type="button" onClick={() => handleOverlayClick('seguir')}>Seguir comprando</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default AvisoContent;
